/**
 * Service middleware for Device UI (dui) PHP API
 * @description This module contains functions to interact with the Device UI (dui) data from the PHP API.
 * Front-end: https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2967
 * PHP API: https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2928
 */

import type {
	DuiBulkDeleteResponse,
	DuiBulkUpdateResponse,
	DuiDeleteResponse,
	DuiListResponse,
	DuiSaveResponse,
	DuiUpdateResponse,
} from 'src/types/responses/DeviceUIResponse';
import { getReq, postReq } from '../http';
import { device_ui } from 'src/stores/features/device-ui';
import type { DeviceUI, DeviceUIList } from 'src/types/DeviceUI';
import { DeviceUIField } from 'src/types/DeviceUI';

/**
 * Update a single DUI field by key
 * POST interface/dui/update
 *
 * @param device_id - Device ID to update
 * @param key - DUI field key to update (e.g., 'furniture')
 * @param value - New value for the specified DUI field
 *
 * @returns Promise<DuiUpdateResponse<Data>>
 */
export async function duiSaveField(
	device_id: string,
	key: DeviceUIField,
	value: DeviceUI[DeviceUIField],
): Promise<DuiUpdateResponse<DeviceUI>> {
	// Validate inputs
	if (!device_id || typeof device_id !== 'string') {
		throw new Error(`Invalid device_id: ${device_id}`);
	}

	if (!key || !Object.values(DeviceUIField).includes(key)) {
		throw new Error(`Invalid key: ${key}`);
	}

	if (!value) {
		throw new Error(`Invalid value: ${value}`);
	}

	const preparedData = {
		id: device_id,
		key,
		data: JSON.stringify(value), // Stringify the data like duiSave does
	};

	const res = await postReq('/interface/dui/update', preparedData);
	if (res?.isok) {
		device_ui.updateDeviceUIField(device_id, key, value);
	} else {
		console.error('DUI field update failed:', res);
	}
	return res as DuiUpdateResponse<DeviceUI>;
}

/**
 * Save or update full device DUI data
 * POST interface/dui/save
 *
 * @param device_id - Device ID
 * @param data - Device DUI data object to save/update
 *
 * @returns Promise<DuiSaveResponse<Data>>
 */
export async function duiSave(device_id: string, data: DeviceUI): Promise<DuiSaveResponse<DeviceUI>> {
	const preparedData = {
		id: device_id,
		data: JSON.stringify(data),
	};
	const res = await postReq('/interface/dui/save', preparedData);
	if (res?.isok) {
		device_ui.updateDeviceUI(device_id, data);
	}

	return res as DuiSaveResponse<DeviceUI>;
}

/**
 * Delete DUI data for a device
 * POST interface/dui/delete
 *
 * @param device_id - Device ID to delete DUI data for
 *
 * @returns Promise<DuiDeleteResponse>
 */
export async function duiDelete(device_id: string): Promise<DuiDeleteResponse> {
	const res = await postReq('/interface/dui/delete', { id: device_id });
	if (res?.isok) {
		device_ui.removeDevice(device_id);
	}
	return res as DuiDeleteResponse;
}

/**
 * Bulk update multiple devices DUI data
 * POST interface/dui/bulk_update
 *
 * @param devices_data - Object mapping device IDs to their DUI data
 *
 * @returns Promise<DuiBulkUpdateResponse<DeviceUIList>>
 */
export async function duiBulkUpdate(
	devices_data: DeviceUIList,
): Promise<DuiBulkUpdateResponse<DeviceUIList>> {
	const res = await postReq('/interface/dui/bulk_update', {
		devices_data: JSON.stringify(devices_data),
	});
	if (res?.isok) {
		device_ui.bulkUpdateDeviceUI(devices_data);
	}
	return res as DuiBulkUpdateResponse<DeviceUIList>;
}

/**
 * Bulk delete DUI data
 * POST interface/dui/bulk_delete
 *
 * @param device_ids - Array of device IDs to delete DUI data for
 * @param reason - Optional reason for deletion
 *
 * @returns Promise<DuiBulkDeleteResponse>
 */
export async function duiBulkDelete(
	device_ids: string[],
	reason?: string,
): Promise<DuiBulkDeleteResponse> {
	const res = await postReq('/interface/dui/bulk_delete', {
		ids: device_ids,
		reason: reason,
	});
	if (res?.isok) {
		device_ids.forEach((id) => device_ui.removeDevice(id));
	}
	return res as DuiBulkDeleteResponse;
}

/** List all devices with DUI data
 * GET interface/dui/list
 */
export async function duiList(): Promise<DuiListResponse<DeviceUIList>> {
	const res = (await getReq('/interface/dui/list', {})) as DuiListResponse<DeviceUIList>;
	if (res?.isok && res?.data?.dui) {
		device_ui.set(res.data.dui);
	}
	return res;
}
